import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createStoreSettingsApi, type StoreSettingsApi } from "./api.js";
import type { StoreSettingsDraft, StoreSettingsProfile } from "./models.js";

export interface StoreSettingsService {
  loadStore(): Promise<StoreSettingsProfile>;
  saveStore(draft: StoreSettingsDraft): Promise<StoreSettingsProfile>;
}

export function createStoreSettingsService(options: {
  api?: StoreSettingsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
}): StoreSettingsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());
  return {
    loadStore: () => track("merchant_store_settings_loaded", () => api.getStore()),
    saveStore: (draft) =>
      track("merchant_store_settings_saved", () => api.updateStore(draft, idempotencyKeyFactory())),
  };
}

async function track<T>(eventName: string, operation: () => Promise<T>): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    dispatchMerchantTelemetry(eventName, {
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    dispatchMerchantTelemetry(eventName, {
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): StoreSettingsApi {
  if (client === undefined) throw new Error("Store settings service requires an API client.");
  return createStoreSettingsApi(client);
}
